'use client'
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LOCALES, TRANSLATED_PATHS } from '@/translations/config';

const OPTIONS: { code: string; label: string; short: string }[] = [
  { code: '', label: 'English', short: 'EN' },
  ...LOCALES,
];

// Stylized monochrome flag glyphs, inherit currentColor.
// Ported from the ifanyonebuilds.it LanguageSelector; fr added in the same
// style (vertical tricolor with the hoist band filled to tell it apart from it).
const LanguageFlag = ({ code }: { code: string }) => (
  <span aria-hidden="true" className="inline-flex h-[22px] w-[22px] items-center justify-center">
    <svg viewBox="0 -1.5 16 16" className="h-[16px] w-[16px]" fill="none">
      {code === '' && (
        <>
          <rect x="1.5" y="2" width="13" height="9" stroke="currentColor" strokeWidth="1" />
          <path d="M1.5 4H14.5M1.5 6H14.5M1.5 8H14.5M6.5 2V7" stroke="currentColor" strokeWidth=".9" />
          <rect x="1.5" y="2" width="5" height="5" fill="currentColor" />
        </>
      )}
      {code === 'es' && (
        <>
          <rect x="1.5" y="2" width="13" height="9" stroke="currentColor" strokeWidth="1" />
          <path d="M1.5 4.5H14.5M1.5 8.5H14.5" stroke="currentColor" strokeWidth="1.5" />
        </>
      )}
      {code === 'fr' && (
        <>
          <rect x="1.5" y="2" width="13" height="9" stroke="currentColor" strokeWidth="1" />
          <path d="M5.8 2V11M10.2 2V11" stroke="currentColor" strokeWidth="1.2" />
          <rect x="1.5" y="2" width="4.3" height="9" fill="currentColor" />
        </>
      )}
      {code === 'it' && (
        <>
          <rect x="1.5" y="2" width="13" height="9" stroke="currentColor" strokeWidth="1" />
          <path d="M5.8 2V11M10.2 2V11" stroke="currentColor" strokeWidth="1.2" />
        </>
      )}
      {code === 'ja' && (
        <>
          <rect x="1.5" y="2" width="13" height="9" stroke="currentColor" strokeWidth="1" />
          <circle cx="8" cy="6.5" r="2.6" fill="currentColor" />
        </>
      )}
    </svg>
  </span>
);

export default function LanguageSwitcher({ bottom = false }: { bottom?: boolean }) {
  const pathname = usePathname() || '/';
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close when navigating, clicking elsewhere, or pressing Escape
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const match = normalizedPathname.match(/^\/(es|fr|it|ja)(\/.*)?$/);
  const currentCode = match ? match[1] : '';
  const basePath = match ? (match[2] || '') : (normalizedPathname === '/' ? '' : normalizedPathname);
  const current = OPTIONS.find((o) => o.code === currentCode) ?? OPTIONS[0];

  const targetFor = (code: string) => {
    // Keep the reader on the current page when a translation of it exists;
    // otherwise send them to that language's scenario page.
    const base = TRANSLATED_PATHS.includes(basePath) ? basePath : '';
    return code ? `/${code}${base}` : (base || '/');
  };

  return (
    <div className="relative group" ref={containerRef}>
      <button
        className="whitespace-nowrap hover:underline flex items-center gap-1"
        aria-expanded={open}
        aria-label="Language"
        onClick={() => setOpen((o) => !o)}
      >
        {/* <LanguageFlag code={current.code} /> */}
        <span>{current.short}</span>
        <span className="text-[0.6em] relative -top-[1px]">▾</span>
      </button>
      <div className={`absolute ${bottom ? 'bottom-[100%]' : 'top-[100%]'} w-[150px] ${open ? 'block' : 'hidden md:group-hover:block'} text-foreground left-0 md:left-auto md:right-0 font-sans text-sm [font-variant:normal] shadow-lg pb-2 pt-2 rounded bg-background border border-black z-50`}>
        {OPTIONS.map(({ code, label, short }, i, arr) => (
          <span key={short}>
            <Link
              href={targetFor(code)}
              className={`flex justify-between items-baseline py-2 whitespace-nowrap no-underline text-med p-4 hover:bg-[var(--accent)] hover:text-background ${code === currentCode ? 'font-bold' : ''}`}
              onClick={() => setOpen(false)}
            >
              <span className="flex items-center gap-1.5"><LanguageFlag code={code} />{label}</span>
              <span className="text-xs opacity-60">{short}</span>
            </Link>
            {i < arr.length - 1 && <hr className="bg-dull-foreground w-[90%] mx-auto h-0 my-0" />}
          </span>
        ))}
      </div>
    </div>
  );
}
